export type WalletErrorKind =
  | 'rejected'
  | 'disconnected'
  | 'insufficientFunds'
  | 'timeout'
  | 'expired'
  | 'network'
  | 'unavailable'
  | 'invalidRequest'
  | 'unknown';

export class WalletActionError extends Error {
  readonly kind: WalletErrorKind;
  readonly translationKey?: string;

  constructor(kind: WalletErrorKind, translationKey?: string) {
    super(kind);
    this.name = 'WalletActionError';
    this.kind = kind;
    this.translationKey = translationKey;
  }
}

function errorText(error: unknown): string {
  if (error instanceof Error) return `${error.name} ${error.message}`.toLowerCase();
  if (typeof error === 'string') return error.toLowerCase();
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message.toLowerCase();
  }
  return '';
}

function containsAny(message: string, fragments: string[]): boolean {
  return fragments.some((fragment) => message.includes(fragment));
}

export function classifyWalletError(error: unknown): WalletErrorKind {
  if (error instanceof WalletActionError) return error.kind;

  const message = errorText(error);
  if (
    containsAny(message, [
      'reject',
      'declin',
      'cancel',
      'aborted',
      'transaction was not sent',
      'user closed',
    ])
  ) {
    return 'rejected';
  }
  if (containsAny(message, ['disconnect', 'not connected', 'bridge closed'])) {
    return 'disconnected';
  }
  if (
    containsAny(message, [
      'insufficient',
      'not enough',
      'balance is too low',
      'cannot pay',
    ])
  ) {
    return 'insufficientFunds';
  }
  if (containsAny(message, ['timeout', 'timed out', 'time out'])) {
    return 'timeout';
  }
  if (containsAny(message, ['expired', 'validuntil', 'valid until'])) {
    return 'expired';
  }
  if (containsAny(message, ['wrong network', 'wrong chain', 'mainnet only'])) {
    return 'network';
  }
  if (
    containsAny(message, [
      'failed to fetch',
      'network request',
      'service unavailable',
      'temporarily unavailable',
      'websocket',
      'rate limit',
      'too many requests',
      ' 429',
      ' 500',
      ' 502',
      ' 503',
      ' 504',
    ])
  ) {
    return 'unavailable';
  }
  if (
    containsAny(message, [
      'invalid',
      'validation',
      'unsafe',
      'payload',
      'not allowlisted',
      'do not match',
      'mismatch',
      'pool is locked',
      'slippage',
      'no active quote',
      'no active liquidity quote',
      'did not return the allowlisted pool',
      'no pending lp account deposit',
    ])
  ) {
    return 'invalidRequest';
  }
  return 'unknown';
}

export function walletErrorTranslationKey(error: unknown): string {
  if (error instanceof WalletActionError && error.translationKey) {
    return error.translationKey;
  }
  return `walletErrors.${classifyWalletError(error)}`;
}
